import React from 'react';

import { PrimaryButton } from '@components/Button/variants';
import useEventTracking from '@hooks/useEventTracking';
import { ClickTarget, TrackingLocation, click } from '@tracking';
import { Offer } from '@utils/subscriptionCardOptions';

import Benefits from './Benefits';

interface HighlightedOfferProps {
  childWrapper: React.RefObject<HTMLDivElement>;
  offer: Offer;
}

const HighlightedOffer = ({
  childWrapper,
  offer: { benefits, description, href, length, originalPrice, price, title },
}: HighlightedOfferProps) => {
  const discount = originalPrice ? Math.round((1 - price / originalPrice) * 100) : null;
  const { trackEvent } = useEventTracking({ location: TrackingLocation.HighlightedOffer });

  return (
    <aside
      ref={childWrapper}
      className="absolute left-24 right-24 top-48 m-auto border border-solid border-primary-magenta bg-white px-[14px] py-32 sm:left-0 sm:right-0 sm:w-[600px] sm:px-[50px] sm:py-48 md:w-[937px] md:px-[57px] lg:w-[1040px] lg:px-[77px]"
      data-test="value-box"
    >
      <div className="flex flex-col items-center gap-24 sm:gap-32 md:flex-row md:items-start lg:gap-64">
        <div>
          <span className="font-suisse-intl-mono text-overline-md text-primary-magenta">
            Special Offer
          </span>
          <h2 className="my-8 font-suisse-works text-headline-xxs text-black sm:my-16 sm:text-headline-sm">
            A Powerful Tool for Emerging Leaders - Unlock the resources you need to be the
            best-informed person in the office
          </h2>
          {description && (
            <p className="m-0 font-suisse-intl text-body-lg font-light text-black">{description}</p>
          )}
        </div>
        <div className="w-full max-w-[351px] shrink-0 md:max-w-[311px]">
          <h3 className="m-0 text-center font-suisse-works text-headline-xs text-black">{title}</h3>
          <div className="mb-8 flex items-center justify-center">
            <span className="font-suisse-intl text-body-lg font-bold text-black">${price}</span>
            &nbsp;
            {discount && (
              <span className="mr-8 text-overline-md italic text-grayscale-gray">
                {discount}% off
              </span>
            )}
            <span className="font-suisse-intl text-body-lg font-medium text-grayscale-gray">
              / {length}
            </span>
          </div>
          <PrimaryButton
            as="link"
            size="small"
            href={href}
            className="w-full"
            onClickCapture={() => trackEvent(click({ clickTarget: ClickTarget.Subscribe }))}
          >
            Subscribe
          </PrimaryButton>
          <Benefits benefits={benefits} />
        </div>
      </div>
    </aside>
  );
};

export default HighlightedOffer;
